import { OverridableStringUnion } from '@mui/types';
import { forwardRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import MuiLinearProgress, {
  LinearProgressProps as MuiLinearProgressProps,
  LinearProgressPropsColorOverrides,
} from '@mui/material/LinearProgress';

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    internal: true;
  }
}

export interface LinearProgressProps extends MuiLinearProgressProps {
  valueStripes?: number;
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'inherit' | 'internal',
    LinearProgressPropsColorOverrides
  >;
}

const LinearProgressRoot = styled('div', {
  name: 'O2LinearProgress',
  slot: 'Root',
})({
  width: '100%',
  position: 'relative',
  overflow: 'hidden',
});

const LinearProgressStripes = styled('span', {
  name: 'O2LinearProgress',
  slot: 'Stripes',
})({
  width: '100%',
  position: 'absolute',
  left: 0,
  bottom: 0,
  top: 0,
  transition: 'transform 0.2s linear',
  transformOrigin: 'left',
  backgroundSize: '8.72px 6.1px',
  backgroundImage: [
    'linear-gradient(',
    '145deg,',
    'rgb( 255, 255, 255   ) 20%,',
    'rgba(255, 255, 255, 0) 25%,',
    'rgba(255, 255, 255, 0) 50%,',
    'rgb( 255, 255, 255   ) 55%,',
    'rgb( 255, 255, 255   ) 70%,',
    'rgba(255, 255, 255, 0) 75%,',
    'rgba(255, 255, 255, 0) 100%',
    ')',
  ].join(''),
});

const LinearProgress = forwardRef<unknown, LinearProgressProps>((props, ref) => {
  const { valueStripes, ...other } = props;
  const theme = useTheme();
  const transformStyle: Record<string, string> = {};

  if (valueStripes !== undefined) {
    let transform = valueStripes - 100;

    if (theme.direction === 'rtl') {
      transform = -transform;
    }

    transformStyle.transform = `translateX(${transform}%)`;
  }

  return (
    <LinearProgressRoot>
      <MuiLinearProgress {...other} ref={ref} />
      {valueStripes && <LinearProgressStripes style={transformStyle} />}
    </LinearProgressRoot>
  );
});

LinearProgress.displayName = 'LinearProgress';

export default LinearProgress;
